import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, User, UserPlus } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import {getEventParticipationStatusText} from "../../utils/eventTypeTranslation";

const MultiCharacterParticipationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  characters = [], 
  currentParticipations = [],
  eventType = 'OTHER'
}) => {
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [pilots, setPilots] = useState({});
  const [loading, setLoading] = useState(false);

  const canHavePilot = eventType === 'PVP' || eventType === 'BC';

  useEffect(() => {
    if (isOpen) {
      // Pre-select characters that are already confirmed
      const confirmedChars = currentParticipations
        .filter(p => p.status === 'CONFIRMED')
        .map(p => p.characterId);
      
      setSelectedCharacters(confirmedChars);

      // Pre-fill pilot information
      const pilotData = {};
      currentParticipations.forEach(p => {
        if (p.pilot && p.characterId) {
          pilotData[p.characterId] = p.pilot;
        }
      });
      setPilots(pilotData);
    }
  }, [isOpen, currentParticipations]);

  const toggleCharacter = (characterId) => {
    setSelectedCharacters(prev => {
      if (prev.includes(characterId)) {
        // Remove character and their pilot info
        const newPilots = { ...pilots };
        delete newPilots[characterId];
        setPilots(newPilots);
        return prev.filter(id => id !== characterId);
      } else {
        return [...prev, characterId];
      }
    });
  };

  const updatePilot = (characterId, pilotName) => {
    setPilots(prev => ({
      ...prev,
      [characterId]: pilotName
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Prepare participation data for each selected character
      const participations = selectedCharacters.map(charId => ({
        characterId: charId,
        pilot: pilots[charId] || null
      }));

      await onConfirm(participations);
      onClose();
    } catch (error) {
      console.error('Error confirming participations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCharacterById = (id) => characters.find(c => c.id === id);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-2xl max-h-[90vh] overflow-hidden"
        >
          <Card variant="glass" className="relative">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <UserPlus className="w-6 h-6 text-cyan-400" />
                  Confirmar Participação
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Selecione os personagens que participarão do evento
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {characters.length === 0 ? (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-400">Você não tem personagens cadastrados</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Crie um personagem primeiro para participar de eventos
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {characters.map(character => {
                    const isSelected = selectedCharacters.includes(character.id);
                    const currentParticipation = currentParticipations.find(
                      p => p.characterId === character.id
                    );

                    return (
                      <div
                        key={character.id}
                        className={`border rounded-xl p-4 transition-all cursor-pointer ${
                          isSelected
                            ? 'border-cyan-500/50 bg-cyan-500/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                        }`}
                      >
                        {/* Character Selection */}
                        <div
                          className="flex items-center gap-4"
                          onClick={() => toggleCharacter(character.id)}
                        >
                          <div
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? 'border-cyan-500 bg-cyan-500'
                                : 'border-gray-400'
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>

                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                            {character.name?.[0] || '?'}
                          </div>

                          <div className="flex-1">
                            <h4 className="font-semibold text-white">
                              {character.name}
                            </h4>
                            <p className="text-sm text-gray-400">
                              {character.class} • Nível {character.level}
                              {character.totalPoints > 0 && ` • ${character.totalPoints} pts`}
                            </p>
                          </div>

                          {currentParticipation?.status && (
                            <div className={`px-3 py-1 rounded-lg text-xs font-medium ${
                              currentParticipation.status === 'CONFIRMED'
                                ? 'bg-green-500/20 text-green-400'
                                : currentParticipation.status === 'DECLINED'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {getEventParticipationStatusText(currentParticipation.status)}
                            </div>
                          )}
                        </div>

                        {/* Pilot Input (only for PVP/BC and selected characters) */}
                        {isSelected && canHavePilot && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-4 pl-10"
                          >
                            <label className="block text-sm text-gray-400 mb-2">
                              Piloto (opcional)
                            </label>
                            <input
                              type="text"
                              value={pilots[character.id] || ''}
                              onChange={(e) => updatePilot(character.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Nome do piloto..."
                              className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Indique quem irá pilotar este personagem durante o evento
                            </p>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedCharacters.length > 0 && (
                <div className="mt-4 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
                  <p className="text-sm text-cyan-300">
                    ✓ {selectedCharacters.length} personagem{selectedCharacters.length > 1 ? 's' : ''} selecionado{selectedCharacters.length > 1 ? 's' : ''}
                  </p>
                  {canHavePilot && Object.keys(pilots).length > 0 && (
                    <p className="text-xs text-cyan-400 mt-1">
                      {Object.keys(pilots).length} piloto{Object.keys(pilots).length > 1 ? 's' : ''} indicado{Object.keys(pilots).length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || selectedCharacters.length === 0}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
              >
                {loading ? (
                  <>Confirmando...</>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Confirmar {selectedCharacters.length > 0 && `(${selectedCharacters.length})`}
                  </>
                )}
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MultiCharacterParticipationModal;
